import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import DisplayField from 'components/forms/displayField/DisplayField';
import RichTextEditorField from 'components/forms/richTextEditor/RichTextEditorField';
import TextField from 'components/forms/textField/TextField';
import { getLanguageName } from 'i18n/i18n.utils';
import { LanguageCode } from 'i18n/internationalization.types';
import { i18n } from 'i18n/setupInternationalization';
import { MessageLog } from 'models/automation/MessageLogs';
import {
  MessagingType,
  SystemTemplate,
} from 'models/automation/SystemTemplates';
import { TriggerLogType } from 'models/automation/Triggers';
import TriggerEventType from 'pages/automations/common/TriggerEventType';
import TriggerLogTypeIcon from 'pages/automations/common/TriggerLogTypeIcon';
import TriggerName from 'pages/automations/common/TriggerName';
import { StateStyled } from './MessageLogViewModal.styles';
import useMessageLogToolbarSettings from './useMessageLogToolbarSettings';

interface MessageLogViewModalFormElementsProps {
  messageLog: MessageLog;
  template: SystemTemplate | null;
}

const MessageLogViewModalFormElements = ({
  messageLog,
  template,
}: MessageLogViewModalFormElementsProps) => {
  const { t } = useTranslation();

  const messagingType = template?.messagingType;
  const isEmail = messagingType === MessagingType.EMAIL;
  const isMessageLogMigrated = !!messageLog.isMigrated;
  const isTriggerDeleted = !!messageLog.triggerDeleted;
  const hasMigratedRecipients =
    !!messageLog.migratedRecipients && !!messageLog.migratedRecipientsInfo;
  const shouldShowRecipientField =
    !isMessageLogMigrated || hasMigratedRecipients;
  const recipientValue =
    isMessageLogMigrated && hasMigratedRecipients
      ? `${messageLog.migratedRecipients} - ${messageLog.migratedRecipientsInfo}`
      : messageLog.recipient ?? '-';

  const { toolbarSettings, isLoadingVariables } = useMessageLogToolbarSettings({
    messagingType,
  });

  return (
    <Row className="d-flex flex-column">
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="logType"
        label={t('pageAutomations.messageLog.modal.messageStatus')}
        value={
          <StateStyled>
            <TriggerLogTypeIcon type={messageLog.logType} />
            <span>
              {t('pageAutomations.triggers.triggerLogType', {
                context: messageLog.logType,
              })}

              {messageLog.logType === TriggerLogType.FAILURE &&
                `: ${t('pageAutomations.triggers.triggerLogFailureType', {
                  context: messageLog.failureInfo,
                })}`}
            </span>
          </StateStyled>
        }
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="executionTime"
        label={t('pageAutomations.messageLog.modal.executionTime')}
        value={t('common.dateTimeLong', { date: messageLog.executionTime })}
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="trigger"
        label={t('pageAutomations.messageLog.modal.source')}
        value={
          <TriggerName
            formatName={(name) =>
              t('pageAutomations.messageLog.modal.sourceName', { name })
            }
            triggerUid={messageLog.triggerUid}
          />
        }
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="eventType"
        label={t('pageAutomations.messageLog.modal.event')}
        value={<TriggerEventType triggerUid={messageLog.triggerUid} />}
      />
      {!isMessageLogMigrated && (
        <>
          <DisplayField
            colSmInput={8}
            colSmLabel={4}
            colXsInput={8}
            colXsLabel={4}
            id="templateType"
            label={t('pageAutomations.messageLog.modal.templateType')}
            value={t('pageAutomations.systemTemplates.messagingType', {
              context: messagingType,
            })}
          />
          <DisplayField
            colSmInput={8}
            colSmLabel={4}
            colXsInput={8}
            colXsLabel={4}
            id="templateName"
            label={t('pageAutomations.messageLog.modal.templateName')}
            value={template?.name ?? '-'}
          />
        </>
      )}
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="language"
        label={t('pageAutomations.messageLog.modal.language')}
        value={getLanguageName(
          i18n.language,
          messageLog?.templateLanguage ?? LanguageCode.EN,
        )}
      />
      {shouldShowRecipientField ? (
        <TextField
          colSmInput={8}
          colSmLabel={4}
          colXsInput={8}
          colXsLabel={4}
          disabled
          label={t('pageAutomations.messageLog.modal.recipient')}
          name="recipient"
          value={recipientValue}
        />
      ) : null}
      {isEmail && (
        <TextField
          colSmInput={8}
          colSmLabel={4}
          colXsInput={8}
          colXsLabel={4}
          label={t('pageAutomations.messageLog.modal.subject')}
          name="templateSubject"
          disabled={isTriggerDeleted}
        />
      )}
      {!isMessageLogMigrated && (
        <RichTextEditorField
          colSmInput={12}
          colSmLabel={0}
          readOnly={isMessageLogMigrated || isTriggerDeleted}
          isLoading={isLoadingVariables}
          linkMode="edit"
          name="templateContent"
          toolbarSettings={toolbarSettings}
        />
      )}
    </Row>
  );
};

export default MessageLogViewModalFormElements;
